import React, { useState, useEffect } from "react";
import { Box, TextField, Button } from "@mui/material";
import {
  ProcessSection,
  CreateProcessSectionRequest,
} from "../../../api/process-sections";

interface ProcessSectionFormProps {
  section?: ProcessSection | null;
  existingSections?: ProcessSection[];
  onSubmit: (data: CreateProcessSectionRequest) => void;
  loading?: boolean;
}

export function ProcessSectionForm({
  section,
  existingSections = [],
  onSubmit,
  loading = false,
}: ProcessSectionFormProps) {
  const [formData, setFormData] = useState<CreateProcessSectionRequest>({
    sectionNumber: 1,
    name: "",
    description: "",
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    if (section) {
      setFormData({
        sectionNumber: section.sectionNumber,
        name: section.name,
        description: section.description || "",
      });
    } else {
      setFormData({
        sectionNumber: 1,
        name: "",
        description: "",
      });
    }
    setErrors({});
  }, [section]);

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.name.trim()) {
      newErrors.name = "نام بخش الزامی است";
    }

    if (formData.sectionNumber <= 0) {
      newErrors.sectionNumber = "شماره بخش باید بزرگتر از صفر باشد";
    }

    // Check for duplicate section number
    const isDuplicate = existingSections.some(
      (existingSection) =>
        existingSection.sectionNumber === formData.sectionNumber &&
        existingSection.id !== section?.id
    );

    if (isDuplicate) {
      newErrors.sectionNumber =
        "شماره بخش تکراری است. لطفاً شماره دیگری انتخاب کنید.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const handleChange =
    (field: keyof CreateProcessSectionRequest) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = event.target.value;
      setFormData((prev) => ({
        ...prev,
        [field]: field === "sectionNumber" ? parseInt(value) || 1 : value,
      }));

      // Clear error when user starts typing
      if (errors[field]) {
        setErrors((prev) => ({
          ...prev,
          [field]: "",
        }));
      }
    };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
      <TextField
        fullWidth
        label="شماره بخش"
        type="number"
        value={formData.sectionNumber}
        onChange={handleChange("sectionNumber")}
        error={!!errors.sectionNumber}
        helperText={errors.sectionNumber}
        margin="normal"
        required
        disabled={loading}
        inputProps={{ min: 1 }}
      />

      <TextField
        fullWidth
        label="نام بخش"
        value={formData.name}
        onChange={handleChange("name")}
        error={!!errors.name}
        helperText={errors.name}
        margin="normal"
        required
        disabled={loading}
      />

      <TextField
        fullWidth
        label="توضیحات"
        value={formData.description}
        onChange={handleChange("description")}
        margin="normal"
        multiline
        rows={3}
        disabled={loading}
      />

      <Box sx={{ mt: 3, display: "flex", gap: 2, justifyContent: "flex-end" }}>
        <Button
          type="submit"
          variant="contained"
          disabled={loading}
          sx={{ minWidth: 100 }}
        >
          {loading ? "در حال پردازش..." : section ? "بروزرسانی" : "ایجاد"}
        </Button>
      </Box>
    </Box>
  );
}
